import React, { useEffect, useState } from "react";
import { Card, Row, Col } from "antd";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, ArcElement, Tooltip as ChartTooltip, Legend, PointElement, LineElement } from "chart.js";
import ProductPieChart from "./ProductPieChart";
import DualAxisBarChart from "./DualAxisBarChart";
import SummaryCard from "./SummaryCard";
import NoData from "./NoData";
import API_SERVICE from "../../shared/services/api-service";
import "./index.scss";
import SimpleBarChart from "./SimpleBarChart";
import AppLoading from "../../shared/components/AppLoading/AppLoading";
import { getEnumList } from "../../shared/services/Utility";

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ArcElement, ChartTooltip, Legend, PointElement, LineElement);

interface SummaryItem {
    label: string;
    key: string;
    tooltip: string | null;
    value: number | null;
    previousValue: number | null;
    percentChange: number | null;
    delta: string | null;
}

interface ChartComponent {
    label: string;
    axis: string;
    tooltip: string | null;
    values: { id: string; value: string | number }[];
}

interface ChartData {
    type: string;
    filter: string;
    toHide: boolean;
    title: string;
    tooltip: string;
    components: ChartComponent[];
}

interface ApiData {
    summary: SummaryItem[];
    charts: ChartData[];
}

interface ChartFilterItem {
    key: string;
    value: string;
}

type DashboardFetchMode = "parallel" | "sequential" | "batched";
const DASHBOARD_FETCH_MODE: DashboardFetchMode = "sequential";

const Graphs = () => {
    const [summaryData, setSummaryData] = useState<SummaryItem[] | null>(null);
    const [monthDisbursedData, setMonthDisbursedData] = useState<ChartData[] | null>(null);
    const [otherChartsData, setOtherChartsData] = useState<ChartData[] | null>(null);
    const [dateFilter, setDateFilter] = useState<string>("THIS_WEEK");
    const [dateFilters, setDateFilters] = useState<{ key: string; value: string }[]>([]);
    const [chartFilters, setChartFilters] = useState<string[]>([]);
    const [chartFiltersList, setChartFiltersList] = useState<ChartFilterItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // First row and second row chart filter keys
    const firstRowChartKeys = ["MONTH_MONTH_DISBURSED_CASES_AND_AMOUNT", "PRODUCT_WISE_CASE_LOGINS", "PRODUCT_WISE_DISBURSED_CASES", "ATTENDANCE_TREND"];
    const secondRowChartKeys = ["TOP_10_CONNECTORS_BY_DISBURSED_CASES_AND_AMOUNT", "TOP_10_LENDERS_BY_DISBURSED_CASES_AND_AMOUNT"];

    useEffect(() => {
        // Use utility functions to get enum values from localStorage
        const dateFiltersList = getEnumList("DATE_FILTER");
        if (dateFiltersList) {
            setDateFilters(dateFiltersList);
        }

        const chartFiltersList = getEnumList("CHART_FILTER");
        if (chartFiltersList) {
            setChartFilters(chartFiltersList.map((filter: any) => filter.key));
            setChartFiltersList(chartFiltersList);
        }
    }, []);

    const fetchSummaryData = async (filter: string) => {
        try {
            const payload = {
                dashboardRequestInterface: "EMPLOYEE_INTERFACE",
                filters: ["SUMMARY"],
                dateFilter: filter,
                loggedInFrom: "WEB"
            };
            const response = await API_SERVICE.getGraphData(payload);
            setSummaryData(response.data.payload.summary);
            setLoading(false)
        } catch (error) {
            console.error("Error fetching summary data:", error);
            setLoading(false)
        }
    };

    const fetchMonthDisbursedData = async (filter: string) => {
        try {
            const payload = {
                dashboardRequestInterface: "EMPLOYEE_INTERFACE",
                filters: ["MONTH_MONTH_DISBURSED_CASES_AND_AMOUNT"],
                dateFilter: filter,
                loggedInFrom: "WEB"
            };
            const response = await API_SERVICE.getGraphData(payload);
            setMonthDisbursedData(response.data.payload.charts);
            setLoading(false)
        } catch (error) {
            setLoading(false)
            console.error("Error fetching month disbursed data:", error);
        }
    };

    let chartsData : any = [];
    const fetchOtherChartsData = async (filter: string, filterKey: string) => {
        try {
            const payload = {
                dashboardRequestInterface: "EMPLOYEE_INTERFACE",
                filters: [filterKey],
                dateFilter: filter,
                loggedInFrom: "WEB"
            };
            const response = await API_SERVICE.getGraphData(payload);
            // setOtherChartsData(...response.data.payload.charts);
            chartsData = [...chartsData, ...response.data.payload.charts]
            setOtherChartsData(chartsData);
            setLoading(false)
        } catch (error) {
            setLoading(false)
            console.error("Error fetching other charts data:", error);
        }
    };

    // --- Way 1: Sequential (one API call after the other; progressive loading) ---
    const fetchDashboardDataSequential = async () => {
            if (chartFilters.length === 0) return;
            setLoading(true);
            setSummaryData(null);
            setMonthDisbursedData(null);
            setOtherChartsData(null);
            try {
                await fetchSummaryData(dateFilter);
                setLoading(false);
                await fetchMonthDisbursedData(dateFilter);
                const otherKeys = chartFilters.filter(
                    (k) => k !== "SUMMARY" && k !== "MONTH_MONTH_DISBURSED_CASES_AND_AMOUNT"
                );
                for (const filterKey of otherKeys) {
                    await fetchOtherChartsData(dateFilter, filterKey);
                }
            } catch (error) {
                setLoading(false);
            }
        };
    
        // --- Way 2: One batched API call (all filters in one request; single round-trip) ---
        const fetchDashboardDataBatched = async () => {
            if (chartFilters.length === 0) return;
            setLoading(true);
            setSummaryData(null);
            setMonthDisbursedData(null);
            setOtherChartsData(null);
            try {
                const payload = {
                    dashboardRequestInterface: "EMPLOYEE_INTERFACE",
                    filters: chartFilters,
                    dateFilter: dateFilter,
                    loggedInFrom: "WEB"
                };
                const response = await API_SERVICE.getGraphData(payload);
                const payloadData = response?.data?.payload;
                if (payloadData) {
                    setSummaryData(payloadData.summary ?? null);
                    const charts = payloadData.charts ?? [];
                    setMonthDisbursedData(charts.filter((c: ChartData) => c.filter === "MONTH_MONTH_DISBURSED_CASES_AND_AMOUNT"));
                    setOtherChartsData(charts.filter((c: ChartData) => c.filter !== "MONTH_MONTH_DISBURSED_CASES_AND_AMOUNT"));
                }
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };
    
        // --- Way 3: Parallel (7 API calls at the same time; original behaviour) ---
        const fetchDashboardDataParallel = () => {
            if (chartFilters.length === 0) return;
            setLoading(true);
            setSummaryData(null);
            setMonthDisbursedData(null);
            setOtherChartsData(null);
            fetchSummaryData(dateFilter);
            fetchMonthDisbursedData(dateFilter);
            const otherKeys = chartFilters.filter(
                (k) => k !== "SUMMARY" && k !== "MONTH_MONTH_DISBURSED_CASES_AND_AMOUNT"
            );
            otherKeys.forEach((filterKey) => fetchOtherChartsData(dateFilter, filterKey));
        };

    useEffect(() => {
        if (chartFilters.length === 0) return;
        const fetchByMode: Record<DashboardFetchMode, () => void> = {
            batched: fetchDashboardDataBatched,
            sequential: fetchDashboardDataSequential,
            parallel: fetchDashboardDataParallel,
        };
        fetchByMode[DASHBOARD_FETCH_MODE]();
    }, [dateFilter, chartFilters]);

    if (loading && !summaryData && !monthDisbursedData && !otherChartsData?.length) {
        return <AppLoading />;
    }

    if (!loading && !summaryData && !monthDisbursedData && !otherChartsData?.length) {
        return <NoData />;
    }

    // Function to check if chart belongs to the first row
    const isFirstRowChart = (filter: string): boolean => {
        return firstRowChartKeys.includes(filter);
    };

    // Function to check if chart belongs to the second row
    const isSecondRowChart = (filter: string): boolean => {
        return secondRowChartKeys.includes(filter);
    };

    // Function to render summary cards
    const renderSummaryCards = () => {
        return summaryData?.map((item, index) => (
            <Col xs={12} sm={12} md={8} lg={8} xl={4} key={index}>
                <SummaryCard item={item} />
            </Col>
        ));
    };

    // Function to check if chart data is valid
    const isValidChartData = (chart: ChartData) => {
        return chart.components && chart.components.some(component => component.values && component.values !== null);
    };

    // Function to render a chart based on its type
    const renderChart = (chart: ChartData) => {
        return (
            <Card>
                {chart.type === "MULTI_AXIS_BAR_CHART" && (
                    <DualAxisBarChart
                        title={chart.title}
                        tooltip={chart.tooltip}
                        chartsData={[chart]}
                    />
                )}
                {chart.type === "PIE_CHART" && (
                    <ProductPieChart
                        chartsData={[chart]}
                        title={chart.title}
                        tooltip={chart.tooltip}
                    />
                )}
                {chart.type === "BAR_CHART" && (
                    <SimpleBarChart
                        chartsData={[chart]}
                        title={chart.title}
                        tooltip={chart.tooltip}
                    />
                )}
            </Card>
        );
    };

    // Filter charts for first row
    const firstRowCharts = [...(monthDisbursedData || []), ...(otherChartsData || [])]
        .filter(chart => !chart.toHide && isValidChartData(chart) && isFirstRowChart(chart.filter));

    let priorities = ['MONTH_MONTH_DISBURSED_CASES_AND_AMOUNT','PRODUCT_WISE_CASE_LOGINS', 'PRODUCT_WISE_DISBURSED_CASES', 'ATTENDANCE_TREND']
    firstRowCharts.sort((a: any, b: any)=>priorities.indexOf(a.filter) - priorities.indexOf(b.filter));

    // Filter charts for second row
    const secondRowCharts = [...(monthDisbursedData || []), ...(otherChartsData || [])]
        .filter(chart => !chart.toHide && isValidChartData(chart) && isSecondRowChart(chart.filter));

    let prioritiesSec = ['TOP_10_CONNECTORS_BY_DISBURSED_CASES_AND_AMOUNT','TOP_10_LENDERS_BY_DISBURSED_CASES_AND_AMOUNT']
    secondRowCharts.sort((a: any, b: any)=>prioritiesSec.indexOf(a.filter) - prioritiesSec.indexOf(b.filter));

    // Generic function to calculate column width based on number of charts
    const getColSpan = (chartCount: number) => {
        if (chartCount === 0) return 6; // Default if no charts
        if (chartCount === 1) return 24; // Full width
        if (chartCount === 2) return 12; // Half width each
        if (chartCount === 3) return 8; // One-third width each
        return 6; // One-fourth width each (default for 4+ charts)
    };

    return (
        <div className="graphs-container">
            <div className="graphs-header">
                <div className="graphs-filter">
                    <span className="filter-label">Filter</span>
                    <select
                        className="filter-select"
                        aria-label="Filter"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                    >
                        {dateFilters.map((filter) => (
                            <option key={filter.key} value={filter.key}>
                                {filter.value}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <Row gutter={[16, 24]} style={{ marginBottom: "24px" }}>
                {renderSummaryCards()}
            </Row>

            {(firstRowCharts.length === 0 && secondRowCharts.length === 0) ? (
                <NoData />
            ) : (
                <>
                    {/* First row of charts */}
                    <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
                        {firstRowCharts.map((chart, index) => (
                            <Col 
                                xs={24} 
                                lg={getColSpan(firstRowCharts.length) < 12 ? 12 : getColSpan(firstRowCharts.length)} 
                                xl={getColSpan(firstRowCharts.length)} 
                                key={`first-row-${index}`}
                                style={{ marginBottom: "16px" }}
                            >
                                {renderChart(chart)}
                            </Col>
                        ))}
                    </Row>

                    {/* Second row of charts */}
                    {secondRowCharts.length > 0 && (
                        <Row gutter={[16, 16]}>
                            {secondRowCharts.map((chart, index) => (
                                <Col 
                                    xs={24} 
                                    lg={12} 
                                    xl={getColSpan(secondRowCharts.length)} 
                                    key={`second-row-${index}`}
                                    style={{ marginBottom: "16px" }}
                                >
                                    {renderChart(chart)}
                                </Col>
                            ))}
                        </Row>
                    )}
                </>
            )}
        </div>
    );
};

export default Graphs;
